import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useFocusEffect } from "expo-router";
import { useCallback, useState } from "react";
import { Pressable, StyleSheet, Text, View } from "react-native";

import { useDocumentViewer } from "./VisitDocumentViewer";

import { fetchVisitDocuments, fetchVisitDocumentUrl, type VisitDocument } from "@/lib/api";
import { colors, fonts, radius, spacing, shadows } from "@/lib/theme";

export function VisitDocumentsCard({ visitId }: { visitId: string }) {
    const [documents, setDocuments] = useState<VisitDocument[]>([]);
    const openDocument = useDocumentViewer();
    useFocusEffect(
        useCallback(() => {
            let active = true;
            setDocuments([]);
            fetchVisitDocuments(visitId)
                .then((result) => {
                    if (active) {
                        setDocuments(result.documents);
                    }
                })
                .catch(() => {
                    if (active) {
                        setDocuments([]);
                    }
                });
            return () => {
                active = false;
            };
        }, [visitId])
    );
    function open(document: VisitDocument) {
        openDocument({
            title: document.title ?? `Document ${document.index + 1}`,
            cacheKey: document.cacheKey ?? `attachment:${visitId}:${document.index}`,
            contentType: document.contentType,
            loadUrl: async () => (await fetchVisitDocumentUrl(visitId, document.index)).url
        });
    }
    if (documents.length === 0) {
        return null;
    }
    return (
        <View style={styles.card} testID="visit-documents">
            <Text style={styles.title}>Documents</Text>
            {documents.map((document) => (
                <Pressable
                    key={document.index}
                    style={styles.document}
                    onPress={() => void open(document)}
                    accessibilityRole="button"
                    accessibilityLabel={`Open ${document.title ?? `Document ${document.index + 1}`}`}
                >
                    <MaterialCommunityIcons
                        name="file-document-outline"
                        size={20}
                        color={colors.info600}
                    />
                    <Text style={styles.name}>
                        {document.title ?? `Document ${document.index + 1}`}
                    </Text>
                    <MaterialCommunityIcons
                        name="chevron-right"
                        size={16}
                        color={colors.textSecondary}
                    />
                </Pressable>
            ))}
        </View>
    );
}
const styles = StyleSheet.create({
    card: {
        marginHorizontal: spacing.base,
        marginTop: spacing.md,
        ...shadows.sm,
        backgroundColor: colors.surface,
        borderRadius: radius.lg,
        padding: spacing.md,
        gap: spacing.sm,
        borderWidth: 1,
        borderColor: colors.border
    },
    title: { fontFamily: fonts.semiBold, fontSize: 15, color: colors.textPrimary },
    document: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.sm,
        paddingVertical: spacing.sm,
        minHeight: 44
    },
    name: { flex: 1, fontFamily: fonts.medium, fontSize: 13, color: colors.info600 },
    error: { fontFamily: fonts.regular, fontSize: 12, color: colors.error600 }
});
